import { BackgroundCategory, BackgroundCheck, BlacklistedCounterparties, SnapshotSection } from '@data/interfaces/verification-snapshot.interface'
import { BLACKLIST_69B_STATUS_LABELS, labelOf } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'

/**
 * Lo que el bloque Análisis deriva de la foto de Syntage y de los indicadores de Ekatena (F-158). Funciones
 * puras: el componente solo decide de dónde sale cada cosa.
 */

/** Cómo se pinta una lista restringida. `clear` = se consultó y no hubo nada; NUNCA se escribe «Limpio». */
export type ListBadgeState = 'found' | 'clear' | 'not-consulted' | 'pending' | 'error'

export interface ListBadge {
  key: 'pep' | 'sanctions' | 'ofac'
  label: string
  state: ListBadgeState
  text: string
  /** «Fuente: Syntage» / «Fuente: Ekatena»; `null` cuando no hay dato que atribuir. */
  source: string | null
}

/** Una contraparte del 69-B en la tabla de detalle de listas. */
export interface BlacklistRow {
  direction: 'Emitida' | 'Recibida'
  name: string
  rfc: string
  status: string
  invoices: number
}

/** Las banderas que trae `syntage_listas_negras_indicadores` (las arma el mapeo de Ekatena). */
export interface ListFlags {
  pep?: boolean
  sanciones?: boolean
  ofac?: boolean
}

/**
 * Lo que trae un análisis de Ekatena. `imported` = `coverage.indicadoresImportados`, la lista de
 * indicadores que de verdad se descargaron; sin ella no se puede distinguir «limpio» de «no vino».
 */
export interface EkatenaLists {
  flags: ListFlags
  imported: string[] | null
}

const NOT_CONSULTED = 'No consultado'

/**
 * Qué indicadores de Ekatena alimentan cada bandera: es el mismo agrupamiento de
 * `api-rendafin/src/modules/verification/providers/ekatena-report.mapper.ts` · `mapearReporteEkatena`,
 * que mete en «sanciones» la ONU, Interpol y el 69-B del SAT.
 */
const EKATENA_SOURCES: Record<ListBadge['key'], string[]> = {
  pep: ['peps'],
  ofac: ['ofac'],
  sanctions: ['united_nations', 'interpol', 'tax_situation'],
}

/**
 * Categoría del background check de Syntage que responde cada lista. Syntage no separa OFAC de las
 * demás sanciones: las dos salen de `international_background`, y por eso se rotulan con su origen.
 */
const SYNTAGE_CATEGORY: Record<ListBadge['key'], string> = {
  pep: 'politically_exposed_person',
  sanctions: 'international_background',
  ofac: 'international_background',
}

const LIST_LABELS: Record<ListBadge['key'], string> = { pep: 'PEP', sanctions: 'Sanciones', ofac: 'OFAC' }
const FLAG_KEYS: Record<ListBadge['key'], keyof ListFlags> = { pep: 'pep', sanctions: 'sanciones', ofac: 'ofac' }

/**
 * Los tres indicadores PEP · Sanciones · OFAC, con este orden de fuentes (F-158, decidido el 2026-10-02):
 *
 *   1. Si el análisis es de **Ekatena** y trae el indicador → «Con hallazgos» / «Sin hallazgos»,
 *      «Fuente: Ekatena».
 *   2. Si hay **foto** con la categoría consultada → «Con hallazgos (n)» / «Sin hallazgos (x de y
 *      bases)», «Fuente: Syntage».
 *   3. Una bandera en `true` de un análisis viejo → «Identificado» (lo único que esa bandera sí dice).
 *   4. Todo lo demás → «No consultado».
 *
 * ! Un `false` sin constancia de que el indicador se consultó NO es «sin hallazgos»: el mapeo de
 * ! Ekatena convierte en `false` tanto «limpio» como «no vino». Por eso sin `imported` el `false` cae
 * ! a «No consultado».
 */
export function listBadgesFrom(input: { background: SnapshotSection<BackgroundCheck> | null | undefined; ekatena: EkatenaLists | null; legacyFlags: ListFlags | null }): ListBadge[] {
  return (['pep', 'sanctions', 'ofac'] as const).map((key) => {
    const label = LIST_LABELS[key]
    return fromEkatena(key, input.ekatena) ?? fromPhoto(key, input.background) ?? fromLegacy(key, input.legacyFlags) ?? { key, label, state: 'not-consulted', text: NOT_CONSULTED, source: null }
  })
}

function fromEkatena(key: ListBadge['key'], ekatena: EkatenaLists | null): ListBadge | null {
  if (!ekatena) return null
  const flagged = ekatena.flags[FLAG_KEYS[key]] === true
  const consulted = ekatena.imported ? EKATENA_SOURCES[key].some((name) => ekatena.imported!.includes(name)) : false
  const base = { key, label: LIST_LABELS[key], source: 'Fuente: Ekatena' }
  if (flagged) return { ...base, state: 'found', text: 'Con hallazgos' }
  if (consulted) return { ...base, state: 'clear', text: 'Sin hallazgos' }
  return null
}

function fromPhoto(key: ListBadge['key'], background: SnapshotSection<BackgroundCheck> | null | undefined): ListBadge | null {
  if (!background) return null
  const base = { key, label: LIST_LABELS[key], source: 'Fuente: Syntage' }
  if (background.status === 'pending') return { ...base, state: 'pending', text: 'Pendiente en Syntage' }
  if (background.status !== 'complete' || !background.data) return null

  const category = background.data.categories?.find((c) => c.category === SYNTAGE_CATEGORY[key])
  if (!category) return null
  if (category.status === 'found') return { ...base, state: 'found', text: `Con hallazgos (${category.recordsCount ?? 0})` }
  if (category.status === 'not_found') {
    const { consulted, total } = databaseCount(category)
    return { ...base, state: 'clear', text: `Sin hallazgos (${consulted} de ${total} bases)` }
  }
  if (category.status === 'error') return { ...base, state: 'error', text: 'Error al consultar' }
  return null
}

function fromLegacy(key: ListBadge['key'], flags: ListFlags | null): ListBadge | null {
  if (flags?.[FLAG_KEYS[key]] !== true) return null
  return { key, label: LIST_LABELS[key], state: 'found', text: 'Identificado', source: null }
}

/** «x de y bases consultadas»: `completed` sobre todas las que Syntage tenía para la categoría. */
export function databaseCount(category: Pick<BackgroundCategory, 'databases'>): { consulted: number; total: number } {
  const databases = category.databases ?? {}
  const total = Object.values(databases).reduce((sum, n) => sum + (Number(n) || 0), 0)
  return { consulted: Number(databases['completed']) || 0, total }
}

/** Las contrapartes 69-B de la foto, ya en la forma de la tabla del panel. */
export function blacklistRowsFromPhoto(data: BlacklistedCounterparties | null | undefined): BlacklistRow[] {
  const rows = (items: BlacklistedCounterparties['issued'] | undefined, direction: BlacklistRow['direction']): BlacklistRow[] =>
    (items ?? []).map((item) => ({
      direction,
      name: item.name || '—',
      rfc: item.rfc || '—',
      status: labelOf(BLACKLIST_69B_STATUS_LABELS, item.blacklistStatus),
      invoices: Number(item.invoices) || 0,
    }))
  return [...rows(data?.issued, 'Emitida'), ...rows(data?.received, 'Recibida')]
}
